/**
 * useDeleteFlow — the ONE shared "ALPACA-style delete" pattern for every form
 * (CLAUDE.md §18.6 / standing rule 11).  Mirrors useSaveFlow for deletes:
 *   1. show the danger confirm ("Delete N …?");
 *   2. reason-for-change gate — if the form has ReasonRequired ON (Settings →
 *      Audit Trail Configuration), prompt for a reason (cancel aborts);
 *   3. run the delete inside the blocking global loader, arming the reason so the
 *      API persists it on the audit row (X-Audit-Reason → AuditData.Comment);
 *   4. show the "Deleted successfully." acknowledgement and call onDeleted.
 *
 * A form gets reason-on-delete for FREE by passing `formName` — no per-form gate
 * code.  This is the GLOBAL delete function: new forms route their delete through
 * here and audit/reason "just work".
 *
 *   const runDelete = useDeleteFlow();
 *   await runDelete({
 *     formName: 'City',
 *     message: `Delete ${n} cit${n === 1 ? 'y' : 'ies'}?`,
 *     delete: async () => { for (const id of ids) await del.mutateAsync(id); },
 *     onDeleted: () => setSelectedIds(new Set()),
 *   });
 */
import { useCallback } from 'react';
import { useConfirm } from './useConfirm';
import { useReasonGate } from './useReasonGate';
import { useNotify } from '../notifications';
import { runLoading } from '../loading';
import { runWithAuditReason } from '../../api/client';
import { FORM_MSG } from '../../formKit/messages';
import { serverMessage } from './serverMessage';
import { resetUnsavedTracking } from '../../hooks/useUnsavedGuard';

export interface DeleteFlowOptions {
  /** Audit form name (matches dbo.AuditData.FormName) — drives the reason gate. */
  formName?: string;
  /** Confirm dialog title (default "Delete"). */
  title?: string;
  /** Confirm message, e.g. "Delete 3 cities?". */
  message: string;
  /** Confirm button label (default "Delete"). */
  confirmLabel?: string;
  /** The actual delete — call the existing command/handler/mutation here. */
  delete: () => Promise<void> | void;
  /** Success acknowledgement (default "Deleted successfully."); pass null to skip the box. */
  successMessage?: string | null;
  /** Run after a successful delete (e.g. clear the selection). */
  onDeleted?: () => void;
  /** Custom failure handler (defaults to an error toast). */
  onError?: (e: unknown) => void;
  /**
   * Opt-in: ALWAYS ask for the reason, whatever Settings → Audit Trail
   * Configuration says for the form — for a removal the business requires a
   * reason on (e.g. PLM Remove layer / dup, prototype v18). The text is the
   * prompt's message; the reason is sent like the gate's (X-Audit-Reason).
   */
  reasonRequired?: { message: string; placeholder?: string };
}

export function useDeleteFlow() {
  const { confirm, alert, prompt } = useConfirm();
  const notify = useNotify();
  const gateReason = useReasonGate();

  return useCallback(
    async (opts: DeleteFlowOptions): Promise<boolean> => {
      const ok = await confirm({
        title: opts.title ?? FORM_MSG.DELETE_TITLE, tone: 'danger',
        confirmLabel: opts.confirmLabel ?? 'Delete', message: opts.message,
      });
      if (!ok) return false;

      // Reason-for-change gate (standing rule 11) — only when the form requires it.
      let reason = '';
      if (opts.reasonRequired) {
        const r = await prompt({
          title: 'Reason for change',
          message: opts.reasonRequired.message,
          placeholder: opts.reasonRequired.placeholder ?? 'Reason…',
          required: true,
        });
        if (r === null || !r.trim()) return false;
        reason = r.trim();
      } else if (opts.formName) {
        const r = await gateReason(opts.formName, 'delete');
        if (r === null) return false;   // user cancelled the reason → abort
        reason = r;
      }

      try {
        await runLoading(runWithAuditReason(reason, async () => { await opts.delete(); }), {
          blocking: true,
          label: 'Deleting…',
        });
        resetUnsavedTracking();   // the record is gone — nothing left to lose
        if (opts.successMessage !== null) {
          await alert({ title: 'Success', message: opts.successMessage ?? FORM_MSG.DELETED, tone: 'success' });
        }
        opts.onDeleted?.();
        return true;
      } catch (e) {
        if (opts.onError) opts.onError(e);
        // The API explains a refused delete (e.g. 409 "Deletion restricted. …"),
        // so show its message; fall back to the generic text otherwise.
        else notify.error(serverMessage(e) ?? FORM_MSG.DELETE_FAILED);
        return false;
      }
    },
    [confirm, alert, prompt, notify, gateReason],
  );
}
